import { useToken } from "@chakra-ui/react";
import { PanInfo } from "framer-motion";
import React, { useCallback, useRef, useState } from "react";

import { getOpenMap } from "../../../store/selectors";
import { usePublicationStore } from "../../../store/store";
import { Layer } from "../../../types/publication";
import { buildRenderRows, rebuildLayersFromOrder, reconstructOrder } from "../utils/reorder-utils";

// Finds the map row (see `data-map-id` on `MapItem` in `left-map-list.tsx`) a drag gesture is
// currently over, if any, by looking up every element stacked under the pointer's position
// (topmost first) and walking each one up to the nearest `data-map-id` ancestor. Uses
// `elementsFromPoint` rather than the singular `elementFromPoint` because the dragged
// Reorder.Item itself is always the topmost element at the pointer (it visually follows the
// cursor via a transform) - the map row underneath it only shows up further down the stack. The
// map list sits directly above this layer list in the same sidebar, so dragging a layer/folder
// upward past the top of the list naturally reaches it.
function getMapElementAtPoint(x: number, y: number): HTMLElement | null {
  const elements = document.elementsFromPoint(x, y);
  for (const element of elements) {
    const mapElement = element.closest<HTMLElement>("[data-map-id]");
    if (mapElement) return mapElement;
  }
  return null;
}

export function useLayerList() {
  const [removeModalFor, setRemoveModalFor] = useState<Layer | null>(null);
  // Close all folders while any folder header is being dragged, so dragging one can't sweep
  // through another open folder's children (checkReorder only swaps one adjacent row at a time).
  const [isDraggingFolder, setIsDraggingFolder] = useState(false);
  // The in-progress drag's flat order, kept purely locally so intermediate drag steps only
  // re-render this component instead of committing to the store (and everything subscribed to
  // it, like the map) on every frame. Only applied to the store once the drag gesture ends, via
  // `handleDragEnd` below.
  const [draftOrder, setDraftOrder] = useState<{
    order: Array<{ id: string; folderId?: string }>;
    expandedFolderIds: Map<string, boolean>;
  } | null>(null);

  const { id: openMapId, layers } = usePublicationStore(getOpenMap);
  const selectLayer = usePublicationStore((state) => state.selectLayer);
  const selectLayerRange = usePublicationStore((state) => state.selectLayerRange);
  const toggleLayerSelected = usePublicationStore((state) => state.toggleLayerSelected);
  const reorderFlatLayers = usePublicationStore((state) => state.reorderFlatLayers);
  const moveLayersToMap = usePublicationStore((state) => state.moveLayersToMap);

  const [dragOverColor] = useToken("colors", ["brand.clear_creek"]);

  // The map row (if any) currently highlighted as a drag target, so it can be un-highlighted
  // again later without needing to know which map it belongs to (see `setDragOverElement` below).
  const dragOverElementRef = useRef<HTMLElement | null>(null);

  // Tracks the last individually clicked layer for shift-range selection
  const anchorLayerIdRef = useRef<string | null>(null);

  const handleLayerClick = useCallback(
    (layer: Layer, e: React.MouseEvent) => {
      // Handle right-click separately for context menu with selection
      if (e.button === 2) {
        // Right-click - only change selection if not already selected and modifiers not held
        if (!layer.selected && !e.ctrlKey && !e.metaKey && !e.shiftKey) {
          selectLayer(layer);
          anchorLayerIdRef.current = layer.id;
        } else if (e.shiftKey && anchorLayerIdRef.current) {
          selectLayerRange(anchorLayerIdRef.current, layer.id);
        } else if (e.ctrlKey || e.metaKey) {
          toggleLayerSelected(layer);
          anchorLayerIdRef.current = layer.id;
        }
        return;
      }

      // Left-click selection logic
      if (e.shiftKey && anchorLayerIdRef.current) {
        selectLayerRange(anchorLayerIdRef.current, layer.id);
      } else if (e.ctrlKey || e.metaKey) {
        toggleLayerSelected(layer);
        anchorLayerIdRef.current = layer.id;
      } else {
        selectLayer(layer);
        anchorLayerIdRef.current = layer.id;
      }
    },
    [selectLayer, selectLayerRange, toggleLayerSelected]
  );

  function removeLayer(layer: Layer) {
    setRemoveModalFor(layer);
  }

  // Only close folders (isDraggingFolder) for the "what to render" pass, not the actual store
  // data - the draft reorder below is reconstructed from the real, untouched `layers`.
  const baseLayers = isDraggingFolder
    ? layers.map((item) => (item.type === "folder" ? { ...item, opened: false } : item))
    : layers;

  const effectiveLayers = draftOrder
    ? rebuildLayersFromOrder(baseLayers, draftOrder.order, draftOrder.expandedFolderIds)
    : baseLayers;

  // Folders can't be nested, so every folder in the tree is a direct child of the root `layers`
  // array - this is all `reconstructOrder` needs to know which ids are folder headers, and whether
  // each is currently open (i.e. whether its children/end-marker are part of `newIds` at all).
  const folderOpenById = new Map(
    effectiveLayers
      .filter((item) => item.type === "folder")
      .map((folder) => [folder.id, folder.opened])
  );

  const rows = buildRenderRows(effectiveLayers);

  // Highlights `element` as the current drag target (and un-highlights the previous one, if any)
  // by toggling its border color directly via the DOM, instead of through React state - this runs
  // on every `onDrag` frame, so re-rendering these list components (or worse, lifting the state up
  // to a shared ancestor with the map) for every frame would be wasteful.
  function setDragOverElement(element: HTMLElement | null) {
    if (dragOverElementRef.current === element) return;
    if (dragOverElementRef.current) {
      dragOverElementRef.current.style.borderColor = "";
    }
    if (element) {
      element.style.borderColor = dragOverColor;
    }
    dragOverElementRef.current = element;
  }

  // Continuously updates which map row (if any) is under the pointer while a layer/folder is
  // being dragged, so it can be highlighted - see `getMapElementAtPoint` and `setDragOverElement`.
  const handleDrag = (_event: MouseEvent | TouchEvent | PointerEvent, info: PanInfo) => {
    setDragOverElement(getMapElementAtPoint(info.point.x, info.point.y));
  };

  // Commits the in-progress drag's last known order (if any) to the store, and resets all local
  // drag state - called once a drag gesture actually ends, instead of on every intermediate step.
  // If the drag ended over a different map in the map list above, move the dragged layer/folder to
  // that map instead of reordering it within this one, discarding any in-progress draft order.
  const handleDragEnd = (
    _event: MouseEvent | TouchEvent | PointerEvent,
    info: PanInfo,
    draggedId: string
  ) => {
    setIsDraggingFolder(false);

    const dropElement = getMapElementAtPoint(info.point.x, info.point.y);
    const dropMapId = dropElement?.dataset.mapId ?? null;
    setDragOverElement(null);

    if (dropMapId && dropMapId !== openMapId) {
      moveLayersToMap([draggedId], dropMapId);
      setDraftOrder(null);
      return;
    }

    if (draftOrder) {
      reorderFlatLayers(draftOrder.order, draftOrder.expandedFolderIds);
    }
    setDraftOrder(null);
  };

  const handleReorder = (newIds: string[]) =>
    setDraftOrder({
      order: reconstructOrder(newIds, folderOpenById),
      expandedFolderIds: folderOpenById,
    });

  return {
    layers,
    effectiveLayers,
    rows,
    removeModalFor,
    setRemoveModalFor,
    removeLayer,
    handleLayerClick,
    handleDrag,
    handleDragEnd,
    handleReorder,
    setIsDraggingFolder,
  };
}
